import { useState, useEffect } from "react";
import { Button, Table } from "antd";
import type {
  FilterValue,
  TableCurrentDataSource,
  SorterResult,
  TablePaginationConfig,
} from "antd/es/table/interface";

import { CloseOutlined } from "@ant-design/icons";
import { useParams } from "react-router";

import { getColumnConfig } from "./DosingConfigTable";
import { checkFiltersAreEmpty } from "../../../utils/tableConfigs/ActivitiesTableUtilFunctions";

// import ComingSoon from "../../../components/UI/ComingSoon";
import InfoTooltip from "../../../components/UI/InfoTooltip";
import GuideButton from "../../../components/UI/GuideButton";
import TableDowloadButton from "../../../components/UI/DownloadTable";
import FetchWrapper from "../../../components/UI/FetchWraper";

const tooltipText =
  "Review detailed information about this subject's study intervention administration and dosing.";

export default function SingleSubjectDosing() {
  const { subjectId } = useParams();
  return (
    <FetchWrapper
      endpointSuffix={`dosing/${subjectId}/`}
      extraQueryKey={subjectId}
    >
      {(data) => <SingleSubjectDosingComponent data={data} />}
    </FetchWrapper>
  );
}

interface SingleSubjectDosingComponentProps {
  data: any[];
}

function SingleSubjectDosingComponent({
  data,
}: SingleSubjectDosingComponentProps) {
  const [dosingTableFilters, setDosingTableFilters] = useState<
    Record<string, FilterValue | null>
  >({});
  const [dosingFilteredData, setDosingFilteredData] = useState<any[]>([]);

  const [tableHeight, setTableHeight] = useState(window.innerHeight - 400);

  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - 520);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  //Creating columns for the table
  const dosingColumns = (getColumnConfig as (...args: any[]) => any)(
    data,
    dosingTableFilters,
    "dosing",
    true,
  );

  //Event handler for filtering the Dosing table
  const onChangeDosingTableFilters = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<any> | SorterResult<any>[],
    extra: TableCurrentDataSource<any>,
  ) => {
    setDosingTableFilters(filters);
    if (checkFiltersAreEmpty(filters)) {
      setDosingFilteredData([]);
    } else {
      setDosingFilteredData(extra.currentDataSource);
    }
  };

  const handleClearAll = () => {
    setDosingTableFilters({});
    setDosingFilteredData([]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h1
            className="text-[15px] font-semibold"
            data-testid="studyintervention-page-subtitle"
          >
            Study Intervention
          </h1>
          <InfoTooltip
            title={tooltipText}
            data-testid="studyintervention-subtitle-tooltip"
          />
        </div>
        <div className="flex gap-[10px] items-center">
          <TableDowloadButton
            data={data}
            name={`individual_subject_profile_study_intervention_${data[0]?.subject}`}
          />
          <GuideButton pageName="/wiki/guides/exploration-singlesubject-ip-administration" />
        </div>
      </div>
      <Button
        type="text"
        className="p-0"
        icon={<CloseOutlined style={{ fontSize: "12px" }} />}
        onClick={handleClearAll}
      >
        Clear all filters
      </Button>
      {/* <div className="flex bg-white items-center p-2 gap-4 rounded-xs mb-2">
        {tableLegend.map((legend) => (
          <div className="flex gap-3 items-center" key={legend.title}>
            {legend.icon}
            <p>{legend.title}</p>
          </div>
        ))}
      </div> */}

      <Table
        bordered
        className="shadow-md"
        columns={dosingColumns}
        dataSource={data}
        rowKey={(record) => record.timepoint}
        scroll={{
          x: 1500,
          y: tableHeight,
        }}
        size="small"
        pagination={false}
        onChange={onChangeDosingTableFilters}
        footer={() => (
          <div className="flex gap-4 rounded-xs">
            <p className="font-bold">Total records: {data.length}</p>
            {dosingFilteredData.length > 0 && (
              <p className="font-bold">
                Selected records: {dosingFilteredData.length}
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
}
